/**
 * Route-corridor geometry helpers.
 *
 * Pure functions over GeoJSON — no React, no Redux, no Mapbox. They operate on
 * the LineString that Mapbox Directions hands back and produce:
 *  - a simplified LineString suitable for distance checks,
 *  - a buffered Polygon for the visual corridor fill,
 *  - a bbox + point-to-line-distance filter for listings.
 *
 * Turf v7 exports each helper as its own module (`@turf/simplify`, …) so we
 * pay only for what we import. The buffered-polygon helper lives in
 * `corridorBuffer.js` instead, so that `@turf/buffer` (and its heavy `jsts`
 * dependency) stays out of this module — which the route-planner ducks import
 * eagerly into the main bundle.
 */

import { bbox } from '@turf/bbox';
import { lineString, point } from '@turf/helpers';
import { pointToLineDistance } from '@turf/point-to-line-distance';
import { simplify } from '@turf/simplify';

// Douglas–Peucker tolerance (degrees). Driving routes from Mapbox already
// return simplified geometry when we ask for `overview=simplified`, but long
// routes still have 500–1500 vertices. ~0.002° ≈ 200 m at mid-latitudes —
// enough to keep the shape but fast enough to run point-to-line-distance
// against every listing in real time when the corridor slider moves.
const SIMPLIFY_TOLERANCE_DEGREES = 0.002;

/**
 * Reduce the vertex count of a Directions LineString so corridor math runs in
 * <5 ms for a 500 km route. Returns the input unchanged if it's too short to
 * meaningfully simplify.
 *
 * @param {import('geojson').LineString | null | undefined} routeLineString
 * @returns {import('geojson').LineString | null}
 */
export const simplifyRouteLineString = (routeLineString) => {
  if (
    !routeLineString ||
    !Array.isArray(routeLineString.coordinates) ||
    routeLineString.coordinates.length < 3
  ) {
    return routeLineString ?? null;
  }
  const feature = lineString(routeLineString.coordinates);
  const simplified = simplify(feature, {
    tolerance: SIMPLIFY_TOLERANCE_DEGREES,
    highQuality: false,
  });
  return simplified?.geometry ?? routeLineString;
};

/**
 * Compute a `[minLng, minLat, maxLng, maxLat]` bbox expanded by `widthKm`
 * around a LineString, without paying the `@turf/buffer` cost. Uses a simple
 * degrees-per-km approximation — fine for deciding which bbox tiles to query
 * from Sharetribe, where over-fetching by a few km at the edges is harmless.
 *
 * @param {import('geojson').LineString | null | undefined} simplifiedLineString
 * @param {number} widthKm
 * @returns {[number, number, number, number] | null}
 */
export const fastCorridorBBox = (simplifiedLineString, widthKm) => {
  const coords = simplifiedLineString?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0 || !(widthKm > 0)) return null;
  let minLng = coords[0][0];
  let minLat = coords[0][1];
  let maxLng = coords[0][0];
  let maxLat = coords[0][1];
  for (let i = 1; i < coords.length; i += 1) {
    const [lng, lat] = coords[i];
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  // 1° latitude ≈ 111 km everywhere; 1° longitude shrinks with cos(lat). Use
  // the bbox's midpoint as the reference latitude — good enough for a bbox
  // that spans <10°, which every realistic driving route does.
  const midLatRad = ((minLat + maxLat) / 2) * (Math.PI / 180);
  const latPadDeg = widthKm / 111;
  const lngPadDeg = widthKm / (111 * Math.max(Math.cos(midLatRad), 0.2));
  return [minLng - lngPadDeg, minLat - latPadDeg, maxLng + lngPadDeg, maxLat + latPadDeg];
};

/**
 * Fall-back bbox of the buffered polygon. Prefer `fastCorridorBBox` when the
 * polygon is not yet computed.
 *
 * @param {import('geojson').Polygon | import('geojson').MultiPolygon | null | undefined} polygon
 * @returns {[number, number, number, number] | null}
 */
export const polygonBBox = (polygon) => {
  if (!polygon) return null;
  const result = bbox({ type: 'Feature', properties: {}, geometry: polygon });
  return Array.isArray(result) && result.length === 4 ? result : null;
};

/**
 * Keep listings whose geolocation is within `widthKm` of the simplified
 * route, and order them by progression along the route (closest to start
 * first, ending at the finish). Uses `@turf/point-to-line-distance` for the
 * corridor gate, and a cheap closest-vertex projection for the sort key —
 * both only require the already-imported turf modules.
 *
 * Ordering algorithm: for each in-corridor listing, find the LineString
 * vertex nearest the listing (planar lat/lng squared distance is fine as an
 * ordering key over modest regions) and use that vertex's cumulative
 * along-line distance as the sort key. Ties (two listings nearest the same
 * vertex) fall back to raw distance to the line so the card closer to the
 * route appears first.
 *
 * Listings without a usable geolocation are filtered out silently.
 *
 * @param {Array<object>} listings  Denormalized Sharetribe listings.
 * @param {import('geojson').LineString | null | undefined} simplifiedLineString
 * @param {number} widthKm
 * @returns {Array<object>}
 */
export const filterListingsInCorridor = (listings, simplifiedLineString, widthKm) => {
  if (
    !Array.isArray(listings) ||
    listings.length === 0 ||
    !simplifiedLineString ||
    !Array.isArray(simplifiedLineString.coordinates) ||
    simplifiedLineString.coordinates.length < 2 ||
    !(widthKm > 0)
  ) {
    return [];
  }
  const coords = simplifiedLineString.coordinates;
  const lineFeature = lineString(coords);

  // Cumulative planar "distance" from the start vertex. Unit is degrees, not
  // km, but that's fine — we only use it to order listings along the same
  // line, so any monotonic function of arc length works.
  const cumulative = new Array(coords.length);
  cumulative[0] = 0;
  for (let i = 1; i < coords.length; i += 1) {
    const [ax, ay] = coords[i - 1];
    const [bx, by] = coords[i];
    cumulative[i] = cumulative[i - 1] + Math.hypot(bx - ax, by - ay);
  }

  const annotated = [];
  for (const listing of listings) {
    const geo = listing?.attributes?.geolocation;
    if (!geo || typeof geo.lat !== 'number' || typeof geo.lng !== 'number') continue;
    const pt = point([geo.lng, geo.lat]);
    const distanceKm = pointToLineDistance(pt, lineFeature, { units: 'kilometers' });
    if (distanceKm > widthKm) continue;

    // Nearest-vertex lookup as a cheap along-track proxy. Degenerate case:
    // the simplified line can degrade to two vertices, in which case we just
    // use the closer endpoint — the sort will still match route direction.
    let closestIdx = 0;
    let closestDistSq = Number.POSITIVE_INFINITY;
    for (let i = 0; i < coords.length; i += 1) {
      const [vx, vy] = coords[i];
      const dx = vx - geo.lng;
      const dy = vy - geo.lat;
      const dSq = dx * dx + dy * dy;
      if (dSq < closestDistSq) {
        closestDistSq = dSq;
        closestIdx = i;
      }
    }

    annotated.push({
      listing,
      sortKey: cumulative[closestIdx],
      distanceKm,
    });
  }

  annotated.sort((a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
    return a.distanceKm - b.distanceKm;
  });

  return annotated.map((a) => a.listing);
};
